import {
  jsonPointerToFormPath,
  type EventMessage,
  type WarningPointer,
} from '@web-config-app/core';

export interface HandleEventMessageResponseParams {
  eventMessage?: EventMessage;
  addConfigMessage: (message: {
    text: string;
    type: 'success' | 'warning';
    formPath?: string;
  }) => void;
  successMessage?: string;
  formatMessage: (descriptor: { id: string }) => string;
}

/**
 * Handles event message response by adding appropriate success or warning messages.
 * This utility is used across different operation handlers (update, validate, custom operations, etc)
 * to provide consistent validation feedback to users.
 *
 * Stores raw warning text + a derived `formPath`. The entity-details
 * page resolves `{{...}}` placeholders and computes a navigable target
 * at render time, where the schema/nodeMap context already lives.
 */
export const handleEventMessageResponse = ({
  eventMessage,
  addConfigMessage,
  successMessage,
  formatMessage,
}: HandleEventMessageResponseParams): void => {
  if (!eventMessage) {
    return;
  }

  const isWarningResponse = eventMessage?.status === 'warning';
  const warnings = isWarningResponse ? eventMessage?.warnings : null;

  // Blocking errors are handled elsewhere (onError) or by subsequent logic;
  // here we only surface success/warning messages.
  if (warnings) {
    warnings.forEach((warning: WarningPointer) => {
      if (!warning?.detail) return;
      addConfigMessage({
        text: warning.detail,
        type: 'warning',
        formPath: jsonPointerToFormPath(warning.source?.pointer),
      });
    });
  } else {
    // no warnings so just a validation pass
    addConfigMessage({
      text:
        eventMessage?.successMessage ||
        successMessage ||
        formatMessage({ id: 'SUCCESS' }),
      type: 'success',
    });
  }
};
